import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { Grid, Button, CircularProgress } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAuthContext } from "../hooks/useAuthContext";
import { useSecureLink } from "../hooks/useSecureLink.js";
import { DATABASE_URL, ELL_URL } from "../appConstants";
import CryptoJS from "crypto-js";
import AuthHeader from "../services/AuthHeader";
import Axios from 'axios'
import { useNavigate } from "react-router-dom";
import { getTestSecurityStatus } from "../services/TestSecurity";
import { getCameraErrorMessage } from "../utils/mediaErrors";

const PROCTORED_TEST_TYPES = [1, 3, 4];

function Guidelines() {
  const { candidate, dispatch } = useAuthContext();
  const { secureLink } = useSecureLink();
  const navigate = useNavigate();
  const [fullTestStatus, setFullTestStatus] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [cameraError, setCameraError] = useState(null);
  const [isVerifyingCamera, setIsVerifyingCamera] = useState(false);

  const handleSecuritySuspension = () => {
    alert("Your test has been suspended due to security warnings.");
    localStorage.removeItem("candidate");
    dispatch({ type: "LOGOUT" });
    navigate("/login");
  };

  const checkSecurityStatus = async () => {
    const securityStatus = await getTestSecurityStatus(candidate.id);
    if (securityStatus.suspended) {
      handleSecuritySuspension();
      return false;
    }
    return true;
  };

  useEffect(() => {
    let active = true;

    const loadTestStatus = async () => {
      setIsLoading(true)
      try {
        const [fullStatusResponse] = await Promise.all([
          Axios.get(
            DATABASE_URL + "/candidate/check_full_tess_status/" + candidate.id,
            {
              headers: AuthHeader(),
            }
          ),
          checkSecurityStatus(),
        ]);

        if (active) {
          setFullTestStatus(fullStatusResponse.data.status)
        }
      } finally {
        if (active) {
          setIsLoading(false)
        }
      }
    };

    loadTestStatus().catch((error) => {
      console.error("Could not verify test status:", error);
      if (active) {
        setIsLoading(false)
      }
    });

    return () => {
      active = false;
    };
  }, [])

  const verifyCameraReady = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(getCameraErrorMessage("NotSupportedError"));
      return false;
    }
    setIsVerifyingCamera(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((t) => t.stop());
      setCameraError(null);
      return true;
    } catch (err) {
      console.warn("Pre-launch camera check failed", err);
      setCameraError(getCameraErrorMessage(err));
      return false;
    } finally {
      setIsVerifyingCamera(false);
    }
  };

  const handleNextStep = async () => {
    setCameraError(null);

    if (!(await checkSecurityStatus())) {
      return;
    }

    if (candidate.type_of_test === 2 || candidate?.last_submit_question_type || fullTestStatus) {
      secureLink("/productive_skills_test");
    } else if (PROCTORED_TEST_TYPES.includes(candidate.type_of_test)) {
      if (!(await verifyCameraReady())) {
        return;
      }

      let configAddress =
        DATABASE_URL + "/auth/api_launch_config/" + candidate.id;

      let encryptedAddress = CryptoJS.AES.encrypt(
        configAddress,
        candidate.accessToken
      ).toString();

      let cookieText =
        "ellToken=" +
        candidate.accessToken +
        ";domain=.elltechnologies.com;path=/";
      document.cookie = cookieText;

      window.open(
        ELL_URL + encryptedAddress + "&token=" + candidate.accessToken,
        "_self"
      );
    }
  };
  return (
    <Layout>
      <h1>Test Guidelines</h1>
      {!isLoading ? (
        <>
          <ol className="test-guidelines-list">
            <li>Do not interact with any other candidate in the exam room. ​</li>
            <li>
              If you have a question, raise your hand and an invigilator will come
              to you.​
            </li>
            <li>Do not access any other webpages while you take the test. ​</li>
            <li>Do not leave your desk during the test. ​</li>
            <li>
              Your test will be monitored to ensure the validity of the results. ​
            </li>
          </ol>
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            {cameraError ? (
              <Grid item style={{ maxWidth: "640px", marginTop: "60px" }}>
                <p
                  role="alert"
                  style={{
                    color: "#c62828",
                    textAlign: "center",
                    margin: "0 0 16px",
                    lineHeight: 1.4,
                  }}
                >
                  {cameraError}
                </p>
              </Grid>
            ) : null}
            <Grid item>
              <Button
                variant="contained"
                color={cameraError ? "error" : "primary"}
                size="large"
                onClick={handleNextStep}
                className="button"
                disabled={isVerifyingCamera}
                startIcon={cameraError ? <RefreshIcon /> : null}
                style={{ marginTop: cameraError ? 0 : "60px" }}
              >
                {isVerifyingCamera
                  ? "Checking camera..."
                  : cameraError
                  ? "Try again"
                  : "I acknowledge"}
              </Button>
            </Grid>
          </Grid>
        </>
      ):(
        <ol className="circular-progress">
          <CircularProgress />
        </ol>
      )}
      
    </Layout>
  );
}

export default Guidelines;
